import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiUsers, FiEdit, FiTrash2, FiFilter, FiSearch,
  FiCalendar, FiPhone, FiMail, FiTarget, FiCheck, FiX
} from 'react-icons/fi';
import { 
  getConsultations, updateConsultationStatus, deleteConsultation 
} from '../../utils/adminApi';

const AdminConsultations = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchConsultations();
  }, [selectedStatus, currentPage]);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 10,
        ...(selectedStatus && { status: selectedStatus })
      };
      
      const data = await getConsultations(params);
      setConsultations(data.consultations);
      setTotalPages(data.pagination.pages);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await updateConsultationStatus(id, newStatus);
      fetchConsultations();
    } catch (error) {
      console.error('Failed to update consultation:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this consultation?')) {
      try {
        await deleteConsultation(id);
        fetchConsultations();
      } catch (error) {
        console.error('Failed to delete consultation:', error);
      }
    }
  };

  const filteredConsultations = consultations.filter(consultation =>
    consultation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    consultation.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-accent-100 text-accent-800';
      case 'completed': return 'bg-secondary-100 text-secondary-800';
      case 'cancelled': return 'bg-danger-100 text-danger-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  const getGoalColor = (goal) => {
    switch (goal) {
      case 'weight_loss': return 'bg-primary-100 text-primary-800';
      case 'weight_gain': return 'bg-secondary-100 text-secondary-800';
      case 'maintenance': return 'bg-accent-100 text-accent-800';
      default: return 'bg-neutral-100 text-neutral-800';
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-heading text-neutral-900 mb-2">
          Consultation Management
        </h1>
        <p className="text-neutral-600">
          Manage consultation requests and track client interactions.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-primary-100 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 input-field"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="pl-10 input-field"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div className="flex items-center justify-end">
            <span className="text-sm text-neutral-600">
              Total: {filteredConsultations.length} consultations
            </span>
          </div>
        </div>
      </div>

      {/* Consultations Table */}
      <div className="bg-white rounded-xl shadow-sm border border-primary-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-2 text-neutral-600">Loading consultations...</p>
          </div>
        ) : filteredConsultations.length === 0 ? (
          <div className="p-8 text-center">
            <FiUsers className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-neutral-900 mb-2">No consultations found</h3>
            <p className="text-neutral-600">
              {searchTerm || selectedStatus 
                ? 'Try adjusting your filters' 
                : 'No consultation requests yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Client Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Goal & Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredConsultations.map((consultation) => (
                  <motion.tr
                    key={consultation.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-neutral-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-neutral-900">
                          {consultation.name}
                        </div>
                        <div className="text-sm text-neutral-600 flex items-center">
                          <FiMail className="w-3 h-3 mr-1" />
                          {consultation.email}
                        </div>
                        <div className="text-sm text-neutral-600 flex items-center">
                          <FiPhone className="w-3 h-3 mr-1" />
                          {consultation.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getGoalColor(consultation.goal)}`}>
                          {consultation.goal?.replace('_', ' ').toUpperCase()}
                        </span>
                        <div className="text-sm text-neutral-600">
                          Age: {consultation.age} • Weight: {consultation.current_weight}kg
                        </div>
                        {consultation.health_conditions && (
                          <div className="text-xs text-neutral-500">
                            Health: {consultation.health_conditions}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={consultation.status}
                        onChange={(e) => handleStatusUpdate(consultation.id, e.target.value)}
                        className={`text-sm rounded-full px-3 py-1 border-0 font-medium ${getStatusColor(consultation.status)}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      <div className="flex items-center">
                        <FiCalendar className="w-4 h-4 mr-1" />
                        {new Date(consultation.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDelete(consultation.id)}
                          className="text-danger-600 hover:text-danger-800 p-1 rounded transition-colors"
                          title="Delete consultation"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 bg-neutral-50 border-t border-neutral-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-600">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm bg-white border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm bg-white border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminConsultations;